import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import ReportLightbox from "@/components/site/ReportLightbox";
import { STRIPE_URL } from "@/lib/links";
import Reveal from "@/components/site/Reveal";
import AreaScoreChart from "@/components/site/AreaScoreChart";
import FramedImage from "@/components/site/FramedImage";
import { track } from "@/lib/analytics";

const INCLUDES = [
  "30 preguntas sobre 6 áreas clave de gestión del talento",
  "Puntuación global y puntuación individual por cada área",
  "Interpretación profesional de cada resultado",
  "Identificación de tus áreas críticas y prioritarias",
  "Informe de resultados enviado a tu email",
  "Resultados inmediatos al completar el diagnóstico",
];
const REPORT_PAGES = [
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/47b682e1d19386f2_DR1.webp",
    label: "Portada del informe",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/b47847bb5aa0b6d0_DR2.webp",
    label: "Resumen ejecutivo y puntuación global",
  },
  {
    src: "/cadena-talento-p3.jpg",
    label: "Tu cadena del talento",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/2db7443df2afb0fb_DR4.webp",
    label: "Diagnóstico por dimensión",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/3987e4dfe9632743_DR5.webp",
    label: "Plan de acción sugerido",
  },
  {
    src: "/diagnostico-trr-portada.jpg",
    label: "Team Readiness Report · Portada",
  },
  {
    src: "/diagnostico-trr-resumen.jpg",
    label: "Team Readiness Report · Estado actual del equipo",
  },
  {
    src: "/diagnostico-trr-mapa-riesgo.jpg",
    label: "Team Readiness Report · Mapa de riesgo por área",
  },
  {
    src: "/diagnostico-trr-lectura-areas.jpg",
    label: "Team Readiness Report · Lectura por áreas",
  },
  {
    src: "/diagnostico-trr-plan-accion.jpg",
    label: "Team Readiness Report · Anexo y plan de acción",
  },
];

export default function Diagnostic() {
  const [page, setPage] = useState(null);

  const openGallery = () => {
    track("diagnostic_report_gallery_open");
    setPage(0);
  };

  return (
    <section
      id="diagnostico"
      data-testid="diagnostic-section"
      className="relative bg-[#06231D] text-[#F7F9F8] py-28 lg:py-40 overflow-hidden grain"
    >
      {/* Ambient orb */}
      <div aria-hidden className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full blur-[140px] opacity-40 brand-gradient" />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(247,249,248,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(247,249,248,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14 lg:mb-20">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="font-serif-italic text-[#C9A84C] text-[9rem] lg:text-[12rem] leading-none block">03</span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#06231D] bg-[#C9A84C] px-3 py-2 rounded-md">El diagnóstico</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="text-[#C9A84C] text-[13px] uppercase tracking-[0.22em] mb-6">
              Antes de mejorar, hay que saber dónde estás
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[22ch] mx-auto">
              Diagnóstico <span className="font-serif-italic font-normal text-[#2EC48A]">rápido</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl mx-auto font-light text-center lg:max-w-none">
              No todas las empresas tienen los mismos problemas de talento. Unas fallan en atracción, otras en onboarding, otras en desarrollo.
            </p>
            <p className="mt-4 text-white/70 text-lg leading-relaxed max-w-2xl mx-auto font-light text-center lg:max-w-none">
              En solo 15 minutos, este diagnóstico estructurado te dice qué áreas de tu empresa están rindiendo por debajo y cuáles son tus prioridades reales ahora mismo.
            </p>
          </Reveal>
        </div>

        {/* Mockup del informe justo encima de la tabla de puntuaciones, ambos alineados en el mismo eje vertical */}
        <div className="mb-16 lg:mb-20 flex flex-col items-center gap-10 lg:gap-14">
          <div
            role="button"
            tabIndex={0}
            onClick={openGallery}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openGallery();
              }
            }}
            data-testid="diagnostic-gallery-open"
            aria-label="Ver un ejemplo de informe real"
            className="cursor-pointer group w-full max-w-[560px]"
          >
            <FramedImage
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/e9028414a754cf9f_mockup1.webp"
              alt="Informe del diagnóstico rápido del Talent Health Check — portada y páginas de resultados."
              variant="square"
              tint="dark"
              bare
              shadow
              className="w-full max-w-[560px] transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <p className="mt-5 text-center text-[11px] uppercase tracking-[0.22em] text-[#C9A84C]/80 group-hover:text-[#C9A84C] transition-colors">
              Haz clic para ver un informe real por dentro
            </p>
          </div>
          <div className="w-full max-w-[820px]">
            <AreaScoreChart />
          </div>
        </div>

        {/* Pricing card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
          data-testid="diagnostic-card"
        >
          {/* Ambient glow */}
          <div
            aria-hidden
            className="absolute -inset-6 rounded-[28px] blur-2xl opacity-30 brand-gradient pointer-events-none"
          />

          <div className="relative rounded-2xl overflow-hidden border border-[#C9A84C]/50 bg-[linear-gradient(160deg,rgba(46,196,138,0.10),rgba(6,35,29,0.55)_50%,rgba(201,168,76,0.08))] shadow-[0_60px_140px_-40px_rgba(46,196,138,0.3),0_30px_60px_-30px_rgba(6,35,29,0.9)] p-8 md:p-14 lg:p-16">
            {/* Corner gold rules */}
            <div className="absolute top-0 left-0 h-px w-32 brand-gradient" />
            <div className="absolute bottom-0 right-0 h-px w-32 brand-gradient" />
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-[110px] opacity-40 brand-gradient pointer-events-none"
            />
            <span className="absolute top-4 left-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute top-4 left-4 w-px h-4 bg-[#C9A84C]" />
            <span className="absolute top-4 right-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute top-4 right-4 w-px h-4 bg-[#C9A84C]" />
            <span className="absolute bottom-4 left-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute bottom-4 left-4 w-px h-4 bg-[#C9A84C]" />
            <span className="absolute bottom-4 right-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute bottom-4 right-4 w-px h-4 bg-[#C9A84C]" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A84C] mb-6">
                  Sin suscripciones · Pago único
                </div>
                <h3 className="font-display text-4xl lg:text-5xl font-medium leading-tight text-white mb-2">
                  Diagnóstico rápido
                </h3>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-8">
                  Parte del Talent Health Check
                </div>
                <div className="flex items-baseline gap-3 mb-8">
                  <span className="font-display text-5xl lg:text-6xl font-semibold text-white leading-none">€47</span>
                  <span className="text-[12px] uppercase tracking-[0.2em] text-white/50">≈ $54 USD</span>
                </div>
                <p className="text-white/60 text-base leading-relaxed font-light max-w-md mb-10">
                  Un diagnóstico estructurado que te da claridad instantánea sobre qué está fallando y por dónde empezar.
                </p>
                <a
                  href={STRIPE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="diagnostic-cta-button"
                  onClick={() => track("cta_click", { location: "diagnostic_card", product: "diagnostic" })}
                  className="group inline-flex items-center gap-3 px-8 h-16 rounded-full brand-gradient text-white text-base font-medium cta-lift"
                >
                  Empieza el diagnóstico
                  <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center group-hover:bg-white/25 transition-colors">
                    <ArrowUpRight size={18} strokeWidth={1.5} />
                  </span>
                </a>

                <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.16em] text-white/55">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EC48A]" />
                    Pago seguro
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EC48A]" />
                    Sin suscripción
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EC48A]" />
                    Resultados en 15 min
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/50 mb-6">Qué incluye</div>
                <ul className="grid grid-cols-1 gap-4">
                  {INCLUDES.map((item, i) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: i * 0.05 }}
                      className="flex items-start gap-4 border-b border-white/8 pb-4"
                    >
                      <span className="mt-0.5 w-7 h-7 rounded-full border border-[#2EC48A]/40 grid place-items-center flex-shrink-0">
                        <Check size={14} strokeWidth={2} className="text-[#2EC48A]" />
                      </span>
                      <span className="text-white/85 text-base font-light leading-relaxed">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {page !== null && (
          <ReportLightbox
            pages={REPORT_PAGES}
            kicker="Ejemplo de informe real · Talent Health Check"
            testIdPrefix="diagnostic"
            index={page}
            onClose={() => setPage(null)}
            onPrev={() => setPage((p) => (p - 1 + REPORT_PAGES.length) % REPORT_PAGES.length)}
            onNext={() => setPage((p) => (p + 1) % REPORT_PAGES.length)}
            onSelect={(i) => setPage(i)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
